// Scene designs for AI-generated templates. Each layout turns one scene of the AI's plan
// (texts, icons) into designed layers, in the chosen theme, with or without the presenter.
// Layout names match the API (Services/AI/AIPlanSpec.cs): hero, cards, features, stats, quote,
// offer, cta.
//
// A layout is called with (scene, theme, presenter, options):
//   options.bg     the scene has a library background or a photo as background (the layout lays a
//                  panel / tint over it so the texts stay readable)
//   options.fill   how many props and illustrations will be placed in the scene (0..3)
//   options.photo  a photo will be shown in the scene's frame (photoSlot)
//   options.art    per card: true when the card shows a library illustration instead of its icon
// It returns { layers, presenter, spots, photoSlot, artSlots }:
//   presenter  where the character stands ({ x, y, in, at, flip }) or null
//   spots      boxes for props and illustrations, best first ({ x, y, w, h, at })
//   photoSlot  the photo's frame ({ x, y, w, h, at }) or null
//   artSlots   per card, the box for its illustration (or null)
// Texts are fitted to their boxes (font size and line count), so long words never overflow.

import {
  W, H, CANVAS, bg, rect, card, circle, ring, dots, blob, icon, stars, burst, quoteMark, phone,
  fitText, buttonWidth, wash,
} from "./aiShapes";

const upper = (s) => String(s || "").toUpperCase();
const has = (s) => !!(s && String(s).trim());
const spot = (x, y, w, h, at = 0.8) => ({ x, y, w, h, at });

function eyebrow(L, txt, x, y, w, color, o = {}) {
  if (!has(txt)) return;
  const [layer] = fitText(upper(txt), x, y, w, 10.5, color, { weight: "700", ls: 0.2, min: 8, ...o });
  L.push(layer);
}

/** Pill button with a centered label; returns its height (0 when there is no label). */
function button(L, label, x, y, fill, ink, o = {}) {
  if (!has(label)) return 0;
  const w = o.w || buttonWidth(label);
  const bx = o.center ? Math.round((W - w) / 2) : x;
  L.push(rect(bx, y, w, 32, fill, { r: 16, in: "zoomIn", at: o.at || 1.2 }));
  const [t] = fitText(label, bx + 8, y + 8, w - 16, 12, ink, { align: "center", weight: "700", min: 9, in: "slideInUp", at: (o.at || 1.2) + 0.15 });
  L.push(t);
  return 32;
}

/** White frame (with a soft shadow) for a photo; the photo itself is added by the builder. */
function photoFrame(L, slot) {
  L.push(card(slot.x - 6, slot.y - 6, slot.w + 12, slot.h + 12, "#ffffff", { r: 10, in: "zoomIn", at: Math.max(0, slot.at - 0.2) }));
}

// ------------------------------------------------------------------------- hero

function hero(s, t, P, o = {}) {
  const fill = o.fill || 0;
  const L = o.bg
    ? [wash(t.dark1, 0.92), dots(22, 18, 7, 3, 11, t.accent, { op: 0.6 })]
    : [
        bg(t.dark1, t.dark2),
        dots(22, 18, 7, 3, 11, t.accent, { op: 0.45 }),
        circle(452, 64, 118, t.accent, { op: 0.1 }),
        circle(455, 210, 92, "#ffffff", { op: 0.06, in: "zoomIn", at: 0.2 }),
      ];
  let spots;
  let photoSlot = null;
  if (P) {
    spots = [spot(232, 168, 112, 112, 1.0), spot(440, 4, 72, 62, 1.3), spot(288, 6, 50, 50, 1.5)];
    if (fill === 0) L.push(ring(296, 266, 16, t.accent, 3, { op: 0.8, in: "zoomIn", at: 0.9 }));
  } else if (o.photo) {
    photoSlot = { x: 352, y: 40, w: 156, h: 206, at: 0.5 };
    if (!o.bg) L.push(blob(318, 22, 230, 230, "", { grad: [t.accent2, t.accent], op: 0.9, in: "zoomIn", at: 0.1 }));
    photoFrame(L, photoSlot);
    spots = [spot(316, 200, 84, 84, 1.1), spot(470, 4, 46, 46, 1.4), spot(290, 6, 46, 46, 1.5)];
  } else {
    if (!o.bg) L.push(blob(318, 22, 230, 230, "", { grad: [t.accent2, t.accent], op: 0.9, in: "zoomIn", at: 0.1 }));
    if (fill === 0 && !o.bg) {
      L.push(phone(388, 50, 88, t.accent, t.accent2, { in: "slideInUp", at: 0.5 }));
      L.push(circle(372, 236, 9, t.accent, { in: "zoomIn", at: 0.9 }));
    }
    spots = [spot(346, 52, 166, 176, 0.5), spot(300, 206, 76, 76, 1.1), spot(470, 6, 46, 46, 1.4)];
    if (fill === 0) L.push(ring(296, 266, 16, t.accent, 3, { op: 0.8, in: "zoomIn", at: 0.9 }));
  }
  eyebrow(L, s.eyebrow, 24, 62, 290, t.accent, { in: "slideInLeft", at: 0.3 });
  L.push(rect(24, 80, 44, 4, t.accent, { r: 2, in: "slideInLeft", at: 0.4 }));
  const [head, hh] = fitText(s.headline, 24, 92, 300, 32, "#ffffff", { weight: "800", maxLines: 2, lh: 1.08, min: 20, in: "slideInLeft", at: 0.5 });
  L.push(head);
  let y = 92 + hh + 8;
  if (has(s.body)) {
    const [body, bh] = fitText(s.body, 24, y, 280, 13, t.pale, { weight: "400", maxLines: 2, lh: 1.35, min: 10, in: "slideInUp", at: 0.9 });
    L.push(body);
    y += bh;
  }
  button(L, s.button, 24, Math.min(y + 14, 250), t.accent, t.onAccent, { at: 1.2 });
  return { layers: L, presenter: P ? { x: 345, y: 70, in: "slideInRight", at: 0.8 } : null, spots, photoSlot };
}

// ------------------------------------------------------------------------- cards

function cards(s, t, P, o = {}) {
  const fill = o.fill || 0;
  const art = o.art || [];
  const L = o.bg
    ? [wash(t.light1, 0.86, "full"), circle(20, 272, 70, t.accent, { op: 0.15 })]
    : [bg(t.light1, t.light2), circle(20, 272, 70, t.accent, { op: 0.15 })];
  if (fill < 2) L.push(dots(P ? 330 : 446, 22, 6, 3, 10, t.accent2, { op: 0.25 }));
  eyebrow(L, s.eyebrow, 24, 26, 300, t.accent2, { in: "slideInLeft", at: 0.2 });
  const headW = P ? 340 : fill >= 2 ? 410 : 476;
  const [head] = fitText(s.headline, 24, 40, headW, 22, t.ink, { weight: "800", min: 15, in: "slideInLeft", at: 0.35 });
  L.push(head);

  const items = (s.items || []).slice(0, 3);
  const cw = P ? 108 : 148;
  const gap = P ? 12 : 16;
  const x0 = P ? 24 : Math.round((W - (items.length * cw + (items.length - 1) * gap)) / 2);
  const artSlots = [];
  items.forEach((it, i) => {
    const x = x0 + i * (cw + gap);
    const y = 86;
    const at = 0.6 + i * 0.3;
    const strong = i === 1;
    L.push(card(x, y, cw, 160, "#ffffff", { r: 14, top: strong ? t.accent : t.accent2, in: "slideInUp", at }));
    if (art[i]) {
      artSlots[i] = { x: Math.round(x + cw / 2 - 28), y: y + 12, w: 56, h: 48, at: at + 0.15 };
    } else {
      artSlots[i] = null;
      L.push(icon(it.icon, x + cw / 2, y + 40, 19, strong ? t.accent : t.accent2, strong ? t.onAccent : "#ffffff", { in: "zoomIn", at: at + 0.15 }));
    }
    L.push(fitText(it.title, x + 6, y + 70, cw - 12, 13, t.ink, { align: "center", weight: "700", min: 10, in: "slideInUp", at: at + 0.2 })[0]);
    L.push(fitText(it.text, x + 8, y + 92, cw - 16, 9.5, t.muted, { align: "center", weight: "400", maxLines: 4, lh: 1.35, min: 8, in: "slideInUp", at: at + 0.25 })[0]);
  });
  const spots = P
    ? [spot(330, 230, 60, 60, 1.6), spot(452, 6, 58, 58, 1.2)]
    : [spot(446, 228, 64, 64, 1.7), spot(452, 4, 56, 56, 1.2), spot(14, 228, 58, 58, 1.8)];
  return { layers: L, presenter: P ? { x: 372, y: 70, in: "slideInRight", at: 0.4 } : null, spots, artSlots };
}

// ------------------------------------------------------------------------- features

function features(s, t, P, o = {}) {
  const fill = o.fill || 0;
  const x0 = P ? 184 : 24;
  const rw = P ? 322 : 316;
  const L = o.bg
    ? [wash(t.light1, 0.86, "full"), circle(500, 20, 40, t.accent, { op: 0.18 })]
    : [bg(t.light1, t.light2), blob(-70, 150, 230, 230, t.accent2, { op: 0.12 }), circle(500, 20, 40, t.accent, { op: 0.18 })];
  let spots;
  let photoSlot = null;
  if (!P) {
    if (o.photo) {
      photoSlot = { x: 360, y: 48, w: 148, h: 196, at: 0.4 };
      photoFrame(L, photoSlot);
      spots = [spot(318, 214, 72, 72, 1.2), spot(470, 4, 48, 48, 1.4)];
    } else {
      L.push(blob(352, 46, 196, 196, "", { grad: [t.accent2, t.accent], op: 0.92, in: "zoomIn", at: 0.2 }));
      if (fill === 0) L.push(icon((s.items && s.items[0] && s.items[0].icon) || "star", 450, 144, 44, "#ffffff", t.accent2, { in: "zoomIn", at: 0.6 }));
      if (fill < 2) L.push(ring(500, 56, 18, t.accent, 3, { in: "zoomIn", at: 0.9 }));
      spots = [spot(376, 62, 148, 150, 0.6), spot(470, 226, 48, 48, 1.4), spot(470, 4, 46, 46, 1.5)];
    }
  } else {
    if (fill < 2) L.push(dots(150, 20, 3, 4, 10, t.accent2, { op: 0.3 }));
    spots = [spot(452, 230, 62, 58, 1.8), spot(466, 2, 48, 48, 1.2)];
  }
  const headW = P && fill >= 2 ? 270 : rw;
  eyebrow(L, s.eyebrow, x0, 26, headW, t.accent2, { in: "slideInLeft", at: 0.3 });
  const [head] = fitText(s.headline, x0, 40, headW, 24, t.ink, { weight: "800", min: 15, in: "slideInLeft", at: 0.4 });
  L.push(head);

  (s.items || []).slice(0, 3).forEach((it, i) => {
    const y = 92 + i * 60;
    const at = 0.7 + i * 0.35;
    const strong = i === 1;
    L.push(card(x0, y, rw, 48, "#ffffff", { r: 12, in: P ? "slideInRight" : "slideInLeft", at }));
    L.push(icon(it.icon, x0 + 26, y + 24, 15, strong ? t.accent : t.accent2, strong ? t.onAccent : "#ffffff", { in: "zoomIn", at: at + 0.15 }));
    L.push(fitText(it.title, x0 + 50, y + 7, rw - 62, 13, t.ink, { weight: "700", min: 10, in: "slideInUp", at: at + 0.2 })[0]);
    L.push(fitText(it.text, x0 + 50, y + 26, rw - 62, 10, t.muted, { weight: "400", min: 8, in: "slideInUp", at: at + 0.25 })[0]);
  });
  return { layers: L, presenter: P ? { x: 4, y: 70, in: "slideInLeft", at: 0.3 } : null, spots, photoSlot };
}

// ------------------------------------------------------------------------- stats

function stats(s, t, P, o = {}) {
  const fill = o.fill || 0;
  const x0 = P ? 190 : 24;
  const tw = P ? 318 : 476;
  const items = (s.items || []).slice(0, 3);
  const bw = P ? 98 : 148;
  const gap = P ? 10 : 16;
  const L = o.bg
    ? [wash(t.dark1, 0.84, "full")]
    : [bg(t.dark1, t.dark2), circle(80, 250, 110, "#ffffff", { op: 0.05 })];
  if (fill === 0) L.push(dots(470, 240, 5, 4, 10, t.accent, { op: 0.4 }));
  const headW = fill > 0 ? tw - 60 : tw;
  eyebrow(L, s.eyebrow, x0, 30, headW, t.accent, { in: "slideInRight", at: 0.2 });
  L.push(fitText(s.headline, x0, 44, headW, 24, "#ffffff", { weight: "800", min: 15, in: "slideInRight", at: 0.35 })[0]);
  items.forEach((it, i) => {
    const x = x0 + i * (bw + gap);
    const at = 0.6 + i * 0.3;
    L.push(rect(x, 100, bw, 96, "#ffffff", { r: 14, op: 0.09, in: "zoomIn", at }));
    L.push(fitText(it.title, x + 4, 116, bw - 8, 28, t.accent, { align: "center", weight: "800", min: 16, in: "zoomIn", at: at + 0.15 })[0]);
    L.push(fitText(it.text, x + 6, 156, bw - 12, 10, "#ffffff", { align: "center", weight: "500", maxLines: 2, lh: 1.3, min: 8, in: "slideInUp", at: at + 0.25 })[0]);
  });
  if (has(s.body)) L.push(fitText(s.body, x0, 216, tw, 12, t.pale, { weight: "400", maxLines: 2, lh: 1.35, min: 9, in: "slideInUp", at: 1.6 })[0]);
  const spots = has(s.body) ? [spot(462, 6, 50, 50, 1.2)] : [spot(440, 214, 70, 70, 1.5), spot(462, 6, 50, 50, 1.2)];
  return { layers: L, presenter: P ? { x: 8, y: 70, in: "slideInLeft", at: 0.3 } : null, spots };
}

// ------------------------------------------------------------------------- quote

const moveY = (layer, y) => Object.assign(layer, { y, originalY: y });

function quote(s, t, P, o = {}) {
  const cw = P ? 330 : 340;
  const cx = P ? 170 : Math.round((W - 340) / 2);
  const pad = 22;
  const [q, qh] = fitText(s.body, cx + pad + 2, 0, cw - 2 * pad - 4, 14, t.ink, { weight: "500", maxLines: 4, lh: 1.4, min: 10, in: "slideInUp", at: 0.8 });
  const nameH = has(s.name) ? 20 : 0;
  const roleH = has(s.role) ? 16 : 0;
  const ch = pad + 27 + 10 + qh + 8 + 12 + (nameH ? 6 + nameH : 0) + roleH + pad;
  const cy = Math.max(16, Math.round((H - ch) / 2));
  const L = [
    ...(o.bg
      ? [wash(t.dark1, 0.45, "full")]
      : [bg(t.accent2, t.dark1), circle(P ? 40 : 480, 260, 70, "#ffffff", { op: 0.08 })]),
    dots(P ? 470 : 20, 20, 5, 4, 10, "#ffffff", { op: 0.35 }),
    card(cx, cy, cw, ch, "#ffffff", { r: 18, in: "zoomIn", at: 0.3 }),
    quoteMark(cx + pad + 2, cy + pad, 34, t.accent2, { in: "zoomIn", at: 0.6 }),
  ];
  let y = cy + pad + 27 + 10;
  L.push(moveY(q, y));
  y += qh + 8;
  L.push(stars(cx + pad + 2, y, 5, 12, t.star, { in: "zoomIn", at: 1.2 }));
  y += 12 + 6;
  if (nameH) {
    L.push(fitText(s.name, cx + pad + 2, y, cw - 2 * pad, 13, t.ink, { weight: "700", min: 10, in: "slideInUp", at: 1.35 })[0]);
    y += nameH;
  }
  if (roleH) L.push(fitText(s.role, cx + pad + 2, y, cw - 2 * pad, 10, t.muted, { weight: "400", min: 8, in: "slideInUp", at: 1.45 })[0]);
  // Stickers on the card's corners.
  const spots = [spot(cx + cw - 50, cy + ch - 46, 70, 70, 1.6), spot(cx + cw - 32, cy - 24, 56, 56, 1.0)];
  return { layers: L, presenter: P ? { x: 0, y: 70, in: "slideInLeft", at: 0.4 } : null, spots };
}

// ------------------------------------------------------------------------- offer

function offer(s, t, P, o = {}) {
  const fill = o.fill || 0;
  const bx = P ? 300 : 370;
  const tw = P ? 190 : 240;
  const L = [
    o.bg ? wash(t.warm2, 0.82, "full") : bg(t.warm1, t.warm2),
    dots(22, 250, 8, 3, 11, "#ffffff", { op: 0.45 }),
    circle(500, 270, 60, "#ffffff", { op: 0.12 }),
  ];
  if (fill < 2) L.push(ring(P ? 250 : 270, 40, 30, "#ffffff", 3, { op: 0.5, in: "zoomIn", at: 0.8 }));
  let spots;
  if (has(s.badge)) {
    L.push(burst(bx, 150, 76, t.burst, { in: "zoomIn", at: 0.5 }));
    L.push(fitText(s.badge, bx - 66, 112, 132, 40, t.burstInk, { align: "center", weight: "800", min: 22, in: "zoomIn", at: 0.8 })[0]);
    if (has(s.badgeSub)) L.push(fitText(upper(s.badgeSub), bx - 60, 160, 120, 16, t.burstInk, { align: "center", weight: "700", min: 10, in: "slideInUp", at: 1.0 })[0]);
    spots = P ? [spot(226, 222, 64, 64, 1.1), spot(320, 8, 52, 52, 1.3)] : [spot(430, 190, 88, 88, 1.1), spot(462, 8, 50, 50, 1.3)];
  } else {
    L.push(circle(bx, 150, 70, "#ffffff", { op: 0.16, in: "zoomIn", at: 0.5 }));
    if (fill === 0) L.push(icon("gift", bx, 150, 46, t.burst, t.burstInk, { in: "zoomIn", at: 0.8 }));
    spots = P
      ? [spot(bx - 62, 86, 124, 124, 0.8), spot(320, 8, 52, 52, 1.3)]
      : [spot(bx - 62, 86, 124, 124, 0.8), spot(430, 204, 80, 80, 1.2), spot(462, 8, 50, 50, 1.3)];
  }
  eyebrow(L, s.eyebrow, 24, 62, tw, t.offerInk, { in: "slideInLeft", at: 0.3 });
  const [head, hh] = fitText(s.headline, 24, 80, tw, 30, t.offerInk, { weight: "800", maxLines: 2, lh: 1.05, min: 18, in: "slideInLeft", at: 0.45 });
  L.push(head);
  let y = 80 + hh + 8;
  if (has(s.body)) {
    const [b, bh] = fitText(s.body, 24, y, tw, 11, t.offerInk, { weight: "400", maxLines: 3, lh: 1.35, min: 9, in: "slideInUp", at: 0.9 });
    L.push(b);
    y += bh;
  }
  button(L, s.button, 24, Math.min(y + 14, 250), t.offerBtn, t.offerBtnInk, { at: 1.2 });
  return { layers: L, presenter: P ? { x: 382, y: 70, in: "slideInRight", at: 0.6 } : null, spots };
}

// ------------------------------------------------------------------------- cta

function cta(s, t, P, o = {}) {
  const fill = o.fill || 0;
  const contacts = (s.items || []).filter((it) => has(it.title)).slice(0, 3);
  if (P) {
    const L = o.bg
      ? [wash(t.dark1, 0.92)]
      : [bg(t.dark1, t.dark2), circle(440, 150, 130, t.accent, { op: 0.08 })];
    if (fill === 0) L.push(dots(300, 20, 5, 3, 10, t.accent, { op: 0.6 }));
    if (fill < 2) L.push(ring(306, 262, 18, t.accent, 3, { op: 0.7, in: "zoomIn", at: 1.0 }));
    eyebrow(L, s.eyebrow, 24, 40, 300, t.accent, { in: "slideInLeft", at: 0.3 });
    const [head, hh] = fitText(s.headline, 24, 54, 300, 26, "#ffffff", { weight: "800", maxLines: 2, lh: 1.1, min: 16, in: "slideInLeft", at: 0.45 });
    L.push(head);
    let y = 54 + hh + 6;
    if (has(s.body)) {
      const [b, bh] = fitText(s.body, 24, y, 280, 12, t.pale, { weight: "400", maxLines: 2, lh: 1.35, min: 9, in: "slideInUp", at: 0.8 });
      L.push(b);
      y += bh;
    }
    const bh = button(L, s.button, 24, y + 10, t.accent, t.onAccent, { at: 1.0 });
    y += bh ? bh + 22 : 12;
    contacts.forEach((it, i) => {
      const cy = Math.min(y + i * 24, 268);
      const at = 1.3 + i * 0.2;
      L.push(icon(it.icon, 36, cy + 10, 11, t.accent, t.onAccent, { in: "zoomIn", at }));
      L.push(fitText(it.title, 54, cy + 3, 260, 11, "#ffffff", { weight: "600", min: 8, in: "slideInLeft", at: at + 0.1 })[0]);
    });
    const spots = [spot(290, 16, 70, 70, 1.2), spot(296, 214, 62, 62, 1.5)];
    return { layers: L, presenter: { x: 360, y: 70, in: "slideInRight", at: 0.4 }, spots };
  }

  const L = [
    ...(o.bg
      ? [wash(t.dark1, 0.62, "full")]
      : [bg(t.dark1, t.dark2), circle(70, 250, 70, t.accent2, { op: 0.35 }), circle(470, 60, 46, t.accent, { op: 0.12 })]),
  ];
  if (fill < 3) L.push(dots(24, 20, 6, 3, 11, t.accent, { op: 0.6 }));
  if (fill < 2) L.push(dots(440, 240, 6, 3, 11, t.accent, { op: 0.6 }));
  if (fill < 1) L.push(ring(30, 150, 14, t.accent, 3, { in: "zoomIn", at: 0.8 }));
  eyebrow(L, s.eyebrow, 62, 50, 400, t.accent, { align: "center", in: "slideInUp", at: 0.3 });
  const [head, hh] = fitText(s.headline, 52, 66, 420, 32, "#ffffff", { align: "center", weight: "800", maxLines: 2, lh: 1.08, min: 18, in: "zoomIn", at: 0.5 });
  L.push(head);
  let y = 66 + hh + 6;
  if (has(s.body)) {
    const [b, bh] = fitText(s.body, 82, y, 360, 12, t.pale, { align: "center", weight: "400", maxLines: 2, lh: 1.35, min: 9, in: "slideInUp", at: 0.9 });
    L.push(b);
    y += bh;
  }
  const bh = button(L, s.button, 0, y + 12, t.accent, t.onAccent, { center: true, at: 1.2 });
  y += bh ? bh + 26 : 16;
  if (contacts.length) {
    const line = contacts.map((c) => c.title).join("   •   ");
    L.push(fitText(line, 32, Math.min(y, 262), 460, 11, "#ffffff", { align: "center", weight: "500", min: 8, in: "slideInUp", at: 1.6 })[0]);
  }
  const spots = [spot(22, 196, 84, 84, 1.4), spot(418, 196, 84, 84, 1.6), spot(24, 12, 52, 52, 1.2)];
  return { layers: L, presenter: null, spots };
}

export const LAYOUTS = { hero, cards, features, stats, quote, offer, cta };

// =========================================================================
// Mobile (9:16) and square (1:1)
//
// The same seven designs for the narrow canvases: W = 295, H = 524 (mobile) or 295 (square).
// Mobile: the content is on top, the presenter (or the scene's picture) at the bottom.
// Square: the presenter is smaller and stands in the bottom-right corner; below the headline the
// content keeps clear of it.
// =========================================================================

const NM = 20; // side margin
const ntw = () => W - 2 * NM; // full text width (255)
const isTall = () => H > 400;

/** Where the presenter stands on a narrow canvas (with its size), or null. */
function nPresenter(P, at, inFx = "slideInUp") {
  if (!P) return null;
  if (isTall()) return { x: Math.round((W - P.w) / 2), y: H - P.h, in: inFx, at };
  const scale = 0.62;
  return { x: Math.round(W - P.w * scale - 2), y: Math.round(H - P.h * scale), scale, in: "slideInRight", at };
}

/** Width for content that starts at y and is h tall: narrower where it would meet the presenter (square). */
function nWidth(pres, y, h) {
  if (!pres || isTall() || y + h < pres.y - 4) return ntw();
  return pres.x - 8 - NM;
}

/** Background (theme gradient, or a tint over a library background / photo). */
function nBack(o, c1, c2, op) {
  return o.bg ? wash(c1, op, "full") : bg(c1, c2);
}

// ------------------------------------------------------------------------- hero (narrow)

function heroN(s, t, P, o = {}) {
  const fill = o.fill || 0;
  const tall = isTall();
  const pres = nPresenter(P, 0.8);
  const L = [nBack(o, t.dark1, t.dark2, 0.86), dots(NM, 16, 7, 3, 11, t.accent, { op: 0.5 })];
  if (!o.bg) {
    L.push(circle(W - 10, tall ? 110 : 30, tall ? 110 : 64, t.accent, { op: 0.1 }));
    L.push(circle(30, H - 30, tall ? 120 : 64, "#ffffff", { op: 0.06, in: "zoomIn", at: 0.2 }));
  }
  const y0 = tall ? 64 : 40;
  eyebrow(L, s.eyebrow, NM, y0, ntw(), t.accent, { in: "slideInLeft", at: 0.3 });
  L.push(rect(NM, y0 + 18, 44, 4, t.accent, { r: 2, in: "slideInLeft", at: 0.4 }));
  const [head, hh] = fitText(s.headline, NM, y0 + 30, ntw(), tall ? 32 : 24, "#ffffff", {
    weight: "800", maxLines: tall ? 3 : 2, lh: 1.08, min: 16, in: "slideInLeft", at: 0.5,
  });
  L.push(head);
  let y = y0 + 30 + hh + 8;
  // Square: below the headline the right side is for the picture or the presenter.
  const bw = tall ? ntw() - 10 : 158;
  if (has(s.body)) {
    const [body, bh] = fitText(s.body, NM, y, bw, tall ? 14 : 11, t.pale, {
      weight: "400", maxLines: tall && P ? 2 : 3, lh: 1.35, min: 9, in: "slideInUp", at: 0.9,
    });
    L.push(body);
    y += bh;
  }
  const maxBtn = tall ? (P ? 262 : 268) : H - 44;
  button(L, s.button, NM, Math.min(y + 14, maxBtn), t.accent, t.onAccent, { at: 1.2, w: tall ? undefined : Math.min(150, buttonWidth(s.button)) });

  let spots;
  let photoSlot = null;
  if (P) {
    spots = tall
      ? [spot(8, 444, 62, 62, 1.0), spot(226, 448, 58, 58, 1.3), spot(238, 14, 46, 46, 1.5)]
      : [spot(240, 8, 46, 46, 1.3)];
    if (fill === 0 && tall) L.push(ring(40, 470, 16, t.accent, 3, { op: 0.8, in: "zoomIn", at: 0.9 }));
  } else if (tall && o.photo) {
    photoSlot = { x: Math.round((W - 168) / 2), y: 288, w: 168, h: 224, at: 0.5 };
    if (!o.bg) L.push(blob(28, 262, 240, 240, "", { grad: [t.accent2, t.accent], op: 0.9, in: "zoomIn", at: 0.1 }));
    photoFrame(L, photoSlot);
    spots = [spot(8, 444, 66, 66, 1.1), spot(236, 286, 50, 50, 1.4), spot(238, 14, 46, 46, 1.5)];
  } else if (tall) {
    if (!o.bg) L.push(blob(28, 290, 240, 220, "", { grad: [t.accent2, t.accent], op: 0.9, in: "zoomIn", at: 0.1 }));
    if (fill === 0 && !o.bg) {
      L.push(phone(104, 314, 88, t.accent, t.accent2, { in: "slideInUp", at: 0.5 }));
      L.push(circle(90, 480, 9, t.accent, { in: "zoomIn", at: 0.9 }));
    }
    spots = [spot(52, 300, 190, 200, 0.5), spot(8, 448, 60, 60, 1.1), spot(238, 14, 46, 46, 1.4)];
  } else {
    if (!o.bg) L.push(blob(168, 140, 170, 170, "", { grad: [t.accent2, t.accent], op: 0.9, in: "zoomIn", at: 0.1 }));
    if (fill === 0 && !o.bg) L.push(phone(208, 150, 62, t.accent, t.accent2, { in: "slideInUp", at: 0.5 }));
    spots = [spot(184, 146, 104, 136, 0.5), spot(240, 8, 46, 46, 1.4)];
  }
  return { layers: L, presenter: pres, spots, photoSlot };
}

// ------------------------------------------------------------------------- cards / features (narrow)

/** Header (eyebrow + headline) of the light designs; returns the y below it. */
function nHeader(L, s, t, y0, o = {}) {
  const tall = isTall();
  eyebrow(L, s.eyebrow, NM, y0, ntw(), t.accent2, { in: "slideInLeft", at: 0.2 });
  const [head, hh] = fitText(s.headline, NM, y0 + 14, o.w || ntw(), tall ? 24 : 18, o.ink || t.ink, {
    weight: "800", maxLines: 2, lh: 1.1, min: 13, in: "slideInLeft", at: 0.35,
  });
  L.push(head);
  return y0 + 14 + hh + (tall ? 14 : 10);
}

/** Rows of items (icon or illustration, title, text) between y and bottom. style: "card" | "list". */
function nRows(L, items, t, y, bottom, pres, o = {}) {
  const tall = isTall();
  const n = Math.max(1, items.length);
  const gap = tall ? 10 : 6;
  const rh = Math.max(36, Math.min(tall ? 74 : 54, Math.floor((bottom - y - gap * (n - 1)) / n)));
  const art = o.art || [];
  const artSlots = [];
  items.forEach((it, i) => {
    const ry = y + i * (rh + gap);
    const rw = nWidth(pres, ry, rh);
    const at = 0.6 + i * 0.3;
    const strong = i === 1;
    const ir = Math.min(tall ? 17 : 13, Math.floor(rh / 2) - 6);
    if (o.style === "list") {
      L.push(card(NM, ry, rw, rh, "#ffffff", { r: 12, in: "slideInLeft", at }));
    } else {
      L.push(card(NM, ry, rw, rh, "#ffffff", { r: 14, in: "slideInUp", at }));
      L.push(rect(NM, ry + 8, 4, rh - 16, strong ? t.accent : t.accent2, { r: 2, in: "slideInUp", at }));
    }
    const cx = NM + 12 + ir;
    if (art[i]) {
      artSlots[i] = { x: NM + 8, y: ry + 6, w: 2 * ir + 8, h: rh - 12, at: at + 0.15 };
    } else {
      artSlots[i] = null;
      L.push(icon(it.icon, cx, ry + rh / 2, ir, strong ? t.accent : t.accent2, strong ? t.onAccent : "#ffffff", { in: "zoomIn", at: at + 0.15 }));
    }
    const tx = NM + 2 * ir + 22;
    const tw = rw - (tx - NM) - 10;
    const twoLines = rh >= 42;
    const roomy = rh >= 56;
    const [ti, th] = fitText(it.title, tx, ry + (twoLines ? (roomy ? 7 : 5) : Math.max(4, rh / 2 - 9)), tw, tall && roomy ? 13 : 11, t.ink, {
      weight: "700", min: 9, in: "slideInUp", at: at + 0.2,
    });
    L.push(ti);
    if (twoLines && has(it.text)) {
      L.push(fitText(it.text, tx, ry + (roomy ? 7 : 5) + th + 1, tw, tall && roomy ? 10 : 9, t.muted, {
        weight: "400", maxLines: rh >= 66 ? 2 : 1, lh: 1.3, min: 7.5, in: "slideInUp", at: at + 0.25,
      })[0]);
    }
  });
  const end = y + n * rh + (n - 1) * gap;
  return { artSlots, end };
}

function cardsN(s, t, P, o = {}) {
  const fill = o.fill || 0;
  const tall = isTall();
  const pres = nPresenter(P, 0.4);
  const L = [nBack(o, t.light1, t.light2, 0.88), circle(10, H - 10, tall ? 80 : 60, t.accent, { op: 0.15 })];
  if (fill < 2) L.push(dots(W - 62, 16, 5, 3, 10, t.accent2, { op: 0.25 }));
  const y = nHeader(L, s, t, tall ? 40 : 20, { w: tall ? ntw() : ntw() - 40 });
  const items = (s.items || []).slice(0, 3);
  const bottom = tall ? (P ? 296 : H - 150) : H - 12;
  const { artSlots, end } = nRows(L, items, t, y, bottom, pres, { art: o.art, style: "card" });
  let spots;
  if (P) spots = tall ? [spot(8, 448, 60, 60, 1.6), spot(232, 448, 54, 54, 1.2)] : [spot(240, 6, 46, 46, 1.2)];
  else if (tall) {
    const top = Math.max(end + 14, H - 200);
    spots = [spot(58, top, 180, H - top - 14, 0.9), spot(8, H - 70, 56, 56, 1.7), spot(240, 6, 46, 46, 1.2)];
    if (fill === 0) {
      const size = Math.min(170, H - top - 20);
      L.push(blob(Math.round((W - size) / 2), top, size, size, "", { grad: [t.accent2, t.accent], op: 0.92, in: "zoomIn", at: 0.3 }));
      L.push(icon((items[1] && items[1].icon) || (items[0] && items[0].icon) || "star", W / 2, top + size / 2, Math.round(size * 0.24), "#ffffff", t.accent2, { in: "zoomIn", at: 0.7 }));
    }
  } else spots = [spot(240, 6, 46, 46, 1.2)];
  return { layers: L, presenter: pres, spots, artSlots };
}

function featuresN(s, t, P, o = {}) {
  const fill = o.fill || 0;
  const tall = isTall();
  const pres = nPresenter(P, 0.3, "slideInLeft");
  const L = [nBack(o, t.light1, t.light2, 0.88)];
  if (!o.bg) L.push(blob(-80, tall ? 200 : 120, 220, 220, t.accent2, { op: 0.12 }));
  L.push(circle(W - 20, 20, 40, t.accent, { op: 0.18 }));
  const y = nHeader(L, s, t, tall ? 40 : 20, { w: tall ? ntw() : ntw() - 40 });
  const items = (s.items || []).slice(0, 3);
  let photoSlot = null;
  let spots;
  let bottom;
  if (P) bottom = tall ? 296 : H - 12;
  else if (tall) bottom = o.photo ? 300 : H - 170;
  else bottom = H - 12;
  const { end } = nRows(L, items, t, y, bottom, pres, { style: "list" });
  if (P) spots = tall ? [spot(8, 448, 60, 60, 1.8), spot(232, 448, 54, 54, 1.2)] : [spot(240, 6, 46, 46, 1.2)];
  else if (tall && o.photo) {
    const top = Math.max(end + 16, 312);
    const h = Math.min(H - top - 16, 200);
    const w = Math.round(h * 0.75);
    photoSlot = { x: Math.round((W - w) / 2), y: top, w, h, at: 0.4 };
    photoFrame(L, photoSlot);
    spots = [spot(8, H - 76, 60, 60, 1.2), spot(236, top - 6, 48, 48, 1.4)];
  } else if (tall) {
    const top = Math.max(end + 16, H - 190);
    L.push(blob(58, top, 180, 170, "", { grad: [t.accent2, t.accent], op: 0.92, in: "zoomIn", at: 0.2 }));
    if (fill === 0) L.push(icon((items[0] && items[0].icon) || "star", 148, top + 85, 42, "#ffffff", t.accent2, { in: "zoomIn", at: 0.6 }));
    spots = [spot(78, top + 10, 140, 150, 0.6), spot(8, H - 66, 52, 52, 1.4), spot(240, 6, 46, 46, 1.5)];
  } else spots = [spot(240, 6, 46, 46, 1.2)];
  return { layers: L, presenter: pres, spots, photoSlot };
}

// ------------------------------------------------------------------------- stats (narrow)

function statsN(s, t, P, o = {}) {
  const fill = o.fill || 0;
  const tall = isTall();
  const pres = nPresenter(P, 0.3);
  const L = [nBack(o, t.dark1, t.dark2, 0.84)];
  if (!o.bg) L.push(circle(40, H - 40, tall ? 120 : 80, "#ffffff", { op: 0.05 }));
  if (fill === 0) L.push(dots(W - 60, tall ? 22 : 12, 5, 3, 10, t.accent, { op: 0.4 }));
  const y0 = tall ? 48 : 22;
  eyebrow(L, s.eyebrow, NM, y0, ntw() - 40, t.accent, { in: "slideInRight", at: 0.2 });
  const [head, hh] = fitText(s.headline, NM, y0 + 14, ntw() - (tall ? 0 : 30), tall ? 24 : 18, "#ffffff", {
    weight: "800", maxLines: 2, lh: 1.1, min: 13, in: "slideInRight", at: 0.35,
  });
  L.push(head);
  let y = y0 + 14 + hh + (tall ? 18 : 10);
  const items = (s.items || []).slice(0, 3);
  const n = Math.max(1, items.length);
  if (tall && !P) {
    // One stat per row: big number, label beside it.
    items.forEach((it, i) => {
      const ry = y + i * 74;
      const at = 0.6 + i * 0.3;
      L.push(rect(NM, ry, ntw(), 64, "#ffffff", { r: 14, op: 0.09, in: "zoomIn", at }));
      L.push(fitText(it.title, NM + 10, ry + 14, 100, 28, t.accent, { align: "center", weight: "800", min: 15, in: "zoomIn", at: at + 0.15 })[0]);
      L.push(fitText(it.text, NM + 120, ry + 14, ntw() - 130, 11, "#ffffff", { weight: "500", maxLines: 2, lh: 1.3, min: 8, in: "slideInUp", at: at + 0.25 })[0]);
    });
    y += n * 74;
  } else {
    // Stats side by side.
    const gap = 8;
    const bw = Math.floor((ntw() - gap * (n - 1)) / n);
    const bh = tall ? 96 : 76;
    items.forEach((it, i) => {
      const x = NM + i * (bw + gap);
      const at = 0.6 + i * 0.3;
      L.push(rect(x, y, bw, bh, "#ffffff", { r: 12, op: 0.09, in: "zoomIn", at }));
      L.push(fitText(it.title, x + 3, y + (tall ? 14 : 10), bw - 6, tall ? 24 : 19, t.accent, { align: "center", weight: "800", min: 12, in: "zoomIn", at: at + 0.15 })[0]);
      L.push(fitText(it.text, x + 4, y + (tall ? 50 : 40), bw - 8, 9, "#ffffff", { align: "center", weight: "500", maxLines: 3, lh: 1.25, min: 7, in: "slideInUp", at: at + 0.25 })[0]);
    });
    y += bh;
  }
  if (has(s.body)) {
    const by = y + 12;
    const bw = nWidth(pres, by, 30);
    L.push(fitText(s.body, NM, by, bw, tall ? 12 : 10, t.pale, { weight: "400", maxLines: tall ? 3 : 2, lh: 1.35, min: 8, in: "slideInUp", at: 1.6 })[0]);
    y = by + (tall ? 50 : 30);
  }
  let spots;
  if (P) spots = tall ? [spot(8, 448, 60, 60, 1.5), spot(232, 448, 54, 54, 1.2)] : [spot(240, 4, 46, 46, 1.2)];
  else if (tall) {
    const top = Math.max(y + 16, H - 190);
    spots = [spot(70, top, 156, 150, 1.1), spot(240, 6, 46, 46, 1.2)];
    if (fill === 0) {
      L.push(circle(W / 2, top + 76, 72, t.accent, { op: 0.16, in: "zoomIn", at: 1.0 }));
      L.push(ring(W / 2, top + 76, 56, t.accent, 4, { op: 0.6, in: "zoomIn", at: 1.2 }));
      L.push(icon("chart", W / 2, top + 76, 38, t.accent, t.onAccent, { in: "zoomIn", at: 1.4 }));
    }
  } else spots = [spot(240, 4, 46, 46, 1.2), spot(230, H - 64, 56, 56, 1.5)];
  return { layers: L, presenter: pres, spots };
}

// ------------------------------------------------------------------------- quote (narrow)

function quoteN(s, t, P, o = {}) {
  const tall = isTall();
  const pres = nPresenter(P, 0.4, "slideInLeft");
  const side = !tall && P; // square with the presenter: the card on the left
  const cw = side ? pres.x - 2 - 12 : ntw();
  const cx = side ? 12 : NM;
  const pad = side ? 14 : 20;
  const [q, qh] = fitText(s.body, cx + pad, 0, cw - 2 * pad, tall ? 15 : 12, t.ink, {
    weight: "500", maxLines: tall ? 6 : side ? 6 : 4, lh: 1.4, min: 8, in: "slideInUp", at: 0.8,
  });
  const nameH = has(s.name) ? 18 : 0;
  const roleH = has(s.role) ? 14 : 0;
  const qm = tall ? 30 : 22;
  const ch = pad + qm + 8 + qh + 8 + 11 + (nameH ? 6 + nameH : 0) + roleH + pad;
  const area = tall && P ? pres.y - 8 : H;
  const cy = Math.max(tall ? 24 : 10, Math.round((area - ch) / 2));
  const L = [
    o.bg ? wash(t.dark1, 0.45, "full") : bg(t.accent2, t.dark1),
    ...(o.bg ? [] : [circle(W - 30, H - 30, tall ? 90 : 60, "#ffffff", { op: 0.08 })]),
    dots(W - 56, 12, 5, 3, 10, "#ffffff", { op: 0.35 }),
    card(cx, cy, cw, ch, "#ffffff", { r: 16, in: "zoomIn", at: 0.3 }),
    quoteMark(cx + pad, cy + pad, qm + 4, t.accent2, { in: "zoomIn", at: 0.6 }),
  ];
  let y = cy + pad + qm + 8;
  L.push(moveY(q, y));
  y += qh + 8;
  L.push(stars(cx + pad, y, 5, 11, t.star, { in: "zoomIn", at: 1.2 }));
  y += 11 + 6;
  if (nameH) {
    L.push(fitText(s.name, cx + pad, y, cw - 2 * pad, 12, t.ink, { weight: "700", min: 9, in: "slideInUp", at: 1.35 })[0]);
    y += nameH;
  }
  if (roleH) L.push(fitText(s.role, cx + pad, y, cw - 2 * pad, 9.5, t.muted, { weight: "400", min: 7.5, in: "slideInUp", at: 1.45 })[0]);
  const spots = P
    ? tall
      ? [spot(8, 448, 60, 60, 1.6), spot(cx + cw - 30, cy - 20, 50, 50, 1.0)]
      : [spot(cx + cw - 30, Math.max(2, cy - 16), 44, 44, 1.0)]
    : [spot(cx + cw - 50, Math.min(H - 60, cy + ch - 36), 60, 60, 1.6), spot(cx + cw - 32, Math.max(2, cy - 22), 50, 50, 1.0)];
  return { layers: L, presenter: pres, spots };
}

// ------------------------------------------------------------------------- offer (narrow)

function offerN(s, t, P, o = {}) {
  const fill = o.fill || 0;
  const tall = isTall();
  const pres = nPresenter(P, 0.6);
  const L = [
    o.bg ? wash(t.warm2, 0.82, "full") : bg(t.warm1, t.warm2),
    dots(NM, H - 34, 8, 3, 11, "#ffffff", { op: 0.45 }),
    circle(W - 20, H - 20, 60, "#ffffff", { op: 0.12 }),
  ];
  // The badge (or a gift): top centre on mobile, right on square.
  const r = tall ? (P ? 48 : 74) : P ? 44 : 60;
  const bx = tall ? Math.round(W / 2) : W - r - 12;
  const by = tall ? (P ? 76 : 128) : P ? 60 : 118;
  if (fill < 2 && tall) L.push(ring(40, by - r + 6, 22, "#ffffff", 3, { op: 0.5, in: "zoomIn", at: 0.8 }));
  let spots;
  if (has(s.badge)) {
    L.push(burst(bx, by, r, t.burst, { in: "zoomIn", at: 0.5 }));
    L.push(fitText(s.badge, bx - r * 0.86, by - r * 0.5, r * 1.72, Math.round(r * 0.5), t.burstInk, { align: "center", weight: "800", min: 14, in: "zoomIn", at: 0.8 })[0]);
    if (has(s.badgeSub)) L.push(fitText(upper(s.badgeSub), bx - r * 0.78, by + r * 0.14, r * 1.56, Math.max(9, Math.round(r * 0.2)), t.burstInk, { align: "center", weight: "700", min: 7, in: "slideInUp", at: 1.0 })[0]);
  } else {
    L.push(circle(bx, by, r, "#ffffff", { op: 0.16, in: "zoomIn", at: 0.5 }));
    if (fill === 0) L.push(icon("gift", bx, by, Math.round(r * 0.66), t.burst, t.burstInk, { in: "zoomIn", at: 0.8 }));
  }
  // Texts: below the badge on mobile, on the left on square.
  const tx = NM;
  const tw = tall ? ntw() : W - 2 * r - 12 - NM - 10;
  let y = tall ? by + r + 18 : P ? 22 : 40;
  const tAlign = tall ? "center" : "left";
  eyebrow(L, s.eyebrow, tx, y, tw, t.offerInk, { align: tAlign, in: "slideInLeft", at: 0.3 });
  y += 16;
  const [head, hh] = fitText(s.headline, tx, y, tall ? tw : P ? ntw() - 2 * r - 20 : tw, tall ? 28 : 21, t.offerInk, {
    align: tAlign, weight: "800", maxLines: tall ? 2 : 3, lh: 1.05, min: 14, in: "slideInLeft", at: 0.45,
  });
  L.push(head);
  y += hh + 8;
  if (has(s.body)) {
    const bw = tall ? tw : nWidth(pres, y, 30);
    const [b, bh] = fitText(s.body, tx, y, Math.min(bw, tall ? tw : 160), tall ? 12 : 10, t.offerInk, {
      align: tAlign, weight: "400", maxLines: tall && P ? 1 : 3, lh: 1.35, min: 8, in: "slideInUp", at: 0.9,
    });
    L.push(b);
    y += bh;
  }
  const maxBtn = tall ? (P ? 266 : H - 120) : H - 44;
  const btnY = Math.min(y + 14, maxBtn);
  button(L, s.button, tx, btnY, t.offerBtn, t.offerBtnInk, { at: 1.2, center: tall });
  if (tall && !P && fill === 0) {
    const top = Math.max(btnY + 56, H - 170);
    L.push(blob(Math.round(W / 2 - 75), top, 150, 140, "#ffffff", { op: 0.18, in: "zoomIn", at: 1.3 }));
    L.push(icon(has(s.badge) ? "gift" : "tag", W / 2, top + 70, 34, t.offerBtn, t.offerBtnInk, { in: "zoomIn", at: 1.5 }));
  }
  if (P) spots = tall ? [spot(8, 448, 60, 60, 1.1), spot(232, 448, 54, 54, 1.3)] : [spot(6, H - 56, 46, 46, 1.3)];
  else if (tall) spots = [spot(70, H - 150, 150, 130, 1.1), spot(8, 14, 48, 48, 1.3)];
  else spots = [spot(bx - 40, by + r + 6, 80, Math.max(40, H - by - r - 14), 1.1), spot(6, H - 56, 46, 46, 1.3)];
  return { layers: L, presenter: pres, spots };
}

// ------------------------------------------------------------------------- cta (narrow)

function ctaN(s, t, P, o = {}) {
  const fill = o.fill || 0;
  const tall = isTall();
  const pres = nPresenter(P, 0.4);
  const contacts = (s.items || []).filter((it) => has(it.title)).slice(0, 3);
  const L = [nBack(o, t.dark1, t.dark2, 0.7)];
  if (!o.bg) {
    L.push(circle(40, H - 40, tall ? 90 : 60, t.accent2, { op: 0.35 }));
    L.push(circle(W - 30, 50, 46, t.accent, { op: 0.12 }));
  }
  const center = !(P && !tall); // square with the presenter: left aligned, beside it
  if (fill < 3) L.push(dots(center ? NM : W - 80, 12, 6, 3, 11, t.accent, { op: 0.6 }));
  const align = center ? "center" : "left";
  let y = tall ? (P ? 52 : 120) : P ? 34 : 50;
  eyebrow(L, s.eyebrow, NM, y, ntw(), t.accent, { align, in: "slideInUp", at: 0.3 });
  y += 16;
  const [head, hh] = fitText(s.headline, NM, y, ntw(), tall ? 30 : 22, "#ffffff", {
    align, weight: "800", maxLines: tall ? 3 : 2, lh: 1.08, min: 15, in: "zoomIn", at: 0.5,
  });
  L.push(head);
  y += hh + 6;
  if (has(s.body)) {
    const bw = center ? ntw() - 20 : nWidth(pres, y, 30);
    const [b, bh] = fitText(s.body, center ? NM + 10 : NM, y, bw, tall ? 12 : 10, t.pale, {
      align, weight: "400", maxLines: tall && P ? 2 : 3, lh: 1.35, min: 8, in: "slideInUp", at: 0.9,
    });
    L.push(b);
    y += bh;
  }
  const bh = button(L, s.button, NM, y + 12, t.accent, t.onAccent, { center, at: 1.2 });
  y += bh ? bh + 24 : 16;
  const limit = tall ? (P ? 292 : H - 30) : H - 14;
  contacts.forEach((it, i) => {
    const cy = y + i * 20;
    if (cy + 14 > limit) return;
    const at = 1.4 + i * 0.2;
    const cw = center ? ntw() : nWidth(pres, cy, 14);
    if (center) {
      L.push(fitText(it.title, NM, cy, cw, 11, "#ffffff", { align: "center", weight: "500", min: 8, in: "slideInUp", at })[0]);
    } else {
      L.push(icon(it.icon, NM + 9, cy + 8, 9, t.accent, t.onAccent, { in: "zoomIn", at }));
      L.push(fitText(it.title, NM + 24, cy + 1, cw - 24, 10, "#ffffff", { weight: "600", min: 7.5, in: "slideInLeft", at: at + 0.1 })[0]);
    }
  });
  let spots;
  if (P) spots = tall ? [spot(8, 448, 62, 62, 1.4), spot(226, 448, 60, 60, 1.6)] : [spot(240, 6, 46, 46, 1.2)];
  else if (tall) {
    spots = [spot(14, 26, 70, 70, 1.2), spot(18, H - 96, 80, 80, 1.4), spot(196, H - 96, 80, 80, 1.6)];
    if (fill === 0) {
      L.push(ring(W / 2, H - 92, 46, t.accent, 3, { op: 0.6, in: "zoomIn", at: 1.6 }));
      L.push(icon((contacts[0] && contacts[0].icon) || "calendar", W / 2, H - 92, 30, t.accent, t.onAccent, { in: "zoomIn", at: 1.8 }));
    }
  }
  else spots = [spot(8, H - 62, 56, 56, 1.4), spot(232, H - 62, 56, 56, 1.6), spot(240, 6, 46, 46, 1.2)];
  return { layers: L, presenter: pres, spots };
}

const NARROW = { hero: heroN, cards: cardsN, features: featuresN, stats: statsN, quote: quoteN, offer: offerN, cta: ctaN };

/** The design for a layout name on the current canvas (see setCanvas in aiShapes). */
export function layoutFor(name) {
  const set = CANVAS === "16:9" ? LAYOUTS : NARROW;
  return set[name] || set.cards;
}

/** Hero and features show a photo in a frame (3:4) on these canvases; elsewhere it is the background. */
export const canFramePhoto = () => CANVAS !== "1:1";
